import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Slider,
  IconButton,
  alpha,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import { useMutation } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { PROFILE_IMAGE_URL } from "../../utils/config";

// Helper function to create image from crop
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/jpeg");
  });
};

const ChangeProfilePicture = (props) => {
  const { setToggle, setFile, refetch, open, setOpen, page } = props;

  // USESTATE
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // MUTATION
  const { mutate, isLoading } = useMutation(
    (image) => {
      return axiosPrivate.post(`assets`, image, {
        "Content-Type": "multipart/form-data",
        type: "formData",
      });
    },
    {
      onSuccess: (data) => {
        setFile(data?.data?.data);
        setToggle(false);
        if (page === "edit_profile") {
          refetch();
        }
        setOpen(false);
        setImageSrc(null);
        enqueueSnackbar("Profile picture updated successfully!", {
          variant: "success",
          autoHideDuration: 2000,
        });
      },
      onError: (error) => {
        if (error?.response?.data?.error?.field_error?.length > 0) {
          error?.response?.data?.error?.field_error?.forEach((msg) => {
            enqueueSnackbar(msg.description || "Network Error!", {
              variant: "error",
              preventDuplicate: true,
              autoHideDuration: 2000,
            });
          });
        } else {
          enqueueSnackbar(
            error?.response?.data?.error?.message ||
              error?.message ||
              "Network Error!",
            {
              variant: "error",
              preventDuplicate: true,
              autoHideDuration: 2000,
            }
          );
        }
      },
    }
  );

  // DROPZONE
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    maxFiles: 1,
    multiple: false,
  });

  // CROP HANDLERS
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation
      );
      const file = new File([croppedImage], "profile.jpg", {
        type: "image/jpeg",
      });

      const formData = new FormData();
      formData.set("asset", file);
      formData.set("type", PROFILE_IMAGE_URL);
      mutate(formData);
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Failed to process image", {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const handleRemoveImage = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  // RENDER
  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.1)",
          borderRadius: 3,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(4px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: "550px" },
          maxWidth: "550px",
          m: { xs: 2, sm: 3 },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 20 },
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Upload Profile Picture
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#666",
            "&:hover": {
              bgcolor: alpha("#000", 0.05),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3, mt: 2 }}>
        {!imageSrc ? (
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${
                isDragActive ? "#FFAA00" : alpha("#FFAA00", 0.3)
              }`,
              borderRadius: 3,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              bgcolor: isDragActive
                ? alpha("#FFAA00", 0.05)
                : alpha("#FFAA00", 0.02),
              "&:hover": {
                borderColor: "#FFAA00",
                bgcolor: alpha("#FFAA00", 0.05),
                transform: "translateY(-2px)",
              },
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon
              sx={{
                fontSize: { xs: 48, sm: 64 },
                color: isDragActive ? "#FFAA00" : alpha("#FFAA00", 0.7),
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: 16, sm: 18 },
                color: "#1a1a1a",
              }}
            >
              {isDragActive
                ? "Drop your image here"
                : "Drag & drop your photo here"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 2,
                fontSize: { xs: 13, sm: 14 },
              }}
            >
              or click to browse from your device
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#999",
                fontSize: { xs: 11, sm: 12 },
              }}
            >
              Supported formats: PNG, JPG, JPEG, GIF (Max 5MB)
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: { xs: 300, sm: 400 },
                bgcolor: "#000",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
              />
            </Box>

            {/* Zoom Control */}
            <Box sx={{ mt: 3, px: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                }}
              >
                <ZoomOutIcon sx={{ color: "#666", fontSize: 20 }} />
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, zoom) => setZoom(zoom)}
                  sx={{
                    color: "primary.main",
                    "& .MuiSlider-thumb": {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
                <ZoomInIcon sx={{ color: "#666", fontSize: 20 }} />
              </Box>

              {/* Rotation Control */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <RotateRightIcon sx={{ color: "#666", fontSize: 20 }} />
                <Slider
                  value={rotation}
                  min={0}
                  max={360}
                  step={1}
                  onChange={(e, rotation) => setRotation(rotation)}
                  sx={{
                    color: "primary.main",
                    "& .MuiSlider-thumb": {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
                <Typography sx={{ minWidth: 40, fontSize: 14, color: "#666" }}>
                  {rotation}°
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  textTransform: "none",
                  color: "#666",
                  fontSize: 13,
                  "&:hover": {
                    bgcolor: alpha("#000", 0.05),
                  },
                }}
              >
                Choose Different Image
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
        }}
      >
        <Box
          sx={{
            display: { md: "flex", xs: "none" },
            gap: 1.5,
            width: "100%",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={handleClose}
            sx={{
              py: 1.5,
              borderColor: "#dee2e6",
              color: "#495057",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              ":hover": {
                borderColor: "#adb5bd",
                bgcolor: "#f8f9fa",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={!imageSrc || isLoading}
            sx={{
              py: 1.5,
              bgcolor: "primary.main",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              boxShadow: "0 3px 5px 2px rgba(255, 170, 0, .3)",
              "&:hover": {
                bgcolor: "#ff9500",
                transform: { xs: "none", sm: "translateY(-1px)" },
                boxShadow: {
                  xs: "0 3px 5px 2px rgba(255, 170, 0, .3)",
                  sm: "0 4px 8px 2px rgba(255, 170, 0, .4)",
                },
              },
              "&:disabled": {
                bgcolor: "#9e9e9e",
                color: "#fff",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Save Photo"}
          </Button>
        </Box>
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={!imageSrc || isLoading}
            sx={{
              py: 1.5,
              bgcolor: "primary.main",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              boxShadow: "0 3px 5px 2px rgba(255, 170, 0, .3)",
              "&:disabled": {
                bgcolor: "#9e9e9e",
                color: "#fff",
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Save Photo"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            fullWidth
            onClick={handleClose}
            sx={{
              py: 1.5,
              borderColor: "#dee2e6",
              color: "#495057",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              fontSize: 15,
              ":hover": {
                borderColor: "#adb5bd",
                bgcolor: "#f8f9fa",
              },
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeProfilePicture;
